import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import StatusBlock from "@/components/feature/StatusBlock";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import {
  formatDateTime,
  formatViews,
  getNewsById,
  getTopicById,
  relatedNews,
} from "@/utils/news";
import ShareBar from "./components/ShareBar";

export default function DetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const item = getNewsById(id);

  useDocumentMeta({
    title: item ? `${item.title} - 爆料网内容详情` : "爆料不存在 - 爆料网",
    description: item
      ? item.summary
      : "该条爆料可能已被删除，返回首页浏览最新内容。",
    keywords: item ? `${item.category},${item.tags.join(",")},爆料网` : "爆料网",
  });

  useEffect(() => {
    setLoading(true);
    setError(false);
    const timer = window.setTimeout(() => {
      try {
        if (item) {
          setLoading(false);
        } else {
          setLoading(false);
        }
      } catch {
        setError(true);
        setLoading(false);
      }
    }, 280);
    return () => window.clearTimeout(timer);
  }, [id, item]);

  if (loading) {
    return (
      <PageLayout>
        <div className="mx-auto max-w-[1280px] px-4 md:px-6 py-20 flex flex-col items-center">
          <span className="w-9 h-9 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin" />
          <p className="mt-3 text-sm text-foreground-500">正在加载爆料内容…</p>
        </div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <div className="mx-auto max-w-[900px] px-4 md:px-6 py-16">
          <StatusBlock
            icon="ri-error-warning-line"
            title="内容加载失败"
            description="网络似乎不太稳定，可以稍后重试。"
            actionLabel="重新加载"
            onAction={() => {
              setError(false);
              setLoading(true);
              window.setTimeout(() => setLoading(false), 320);
            }}
            secondaryLabel="返回首页"
            onSecondary={() => navigate("/")}
          />
        </div>
      </PageLayout>
    );
  }

  if (!item) {
    return (
      <PageLayout>
        <div className="mx-auto max-w-[900px] px-4 md:px-6 py-16">
          <StatusBlock
            icon="ri-file-damage-line"
            title="爆料已被删除或不存在"
            description="这条爆料可能已经下线，或者链接有误。你可以回到首页看看刚更新的内容。"
            actionLabel="返回首页"
            onAction={() => navigate("/")}
            secondaryLabel="浏览社会爆料"
            onSecondary={() => navigate("/category/society")}
          />
        </div>
      </PageLayout>
    );
  }

  const topic = getTopicById(item.topicId);
  const related = relatedNews(item, 4);

  return (
    <PageLayout>
      <div className="mx-auto max-w-[1280px] px-4 md:px-6 pt-5 md:pt-7">
        <nav className="flex items-center gap-1.5 text-xs text-foreground-500 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <Link
            to={`/category/${item.categorySlug}`}
            className="hover:text-primary-600 transition-colors cursor-pointer"
          >
            {item.category}
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <span className="text-foreground-800 truncate max-w-[220px] md:max-w-none">
            {item.title}
          </span>
        </nav>

        <div className="mt-5 flex flex-col lg:flex-row gap-6 lg:gap-8 items-start">
          <article className="w-full lg:flex-1 min-w-0 rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-8">
            <div className="flex items-center gap-2 flex-wrap">
              <Link
                to={`/category/${item.categorySlug}`}
                className="inline-flex items-center h-6 px-2 rounded bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-200 transition-colors cursor-pointer"
              >
                {item.category}
              </Link>
              {item.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2 h-6 inline-flex items-center rounded-full bg-secondary-100 text-secondary-800 text-[11px]"
                >
                  # {tag}
                </span>
              ))}
            </div>

            <h1 className="mt-3 text-[24px] md:text-[32px] leading-[1.35] font-black text-foreground-950">
              {item.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500 pb-4 border-b border-background-200">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line" />
                {item.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" />
                {formatDateTime(item.publishedAt)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" />
                {formatViews(item.views)} 次阅读
              </span>
              <span className="ml-auto">
                <ShareBar title={item.title} />
              </span>
            </div>

            <figure className="mt-5">
              <div className="w-full h-[220px] md:h-[380px] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={item.image}
                  alt={item.title}
                  title={`${item.title} - 爆料现场配图`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <figcaption className="mt-2 text-xs text-foreground-400">
                配图为演示素材，与真实事件无关 · 爆料网
              </figcaption>
            </figure>

            <p className="mt-5 rounded-md bg-background-100 border-l-2 border-primary-500 px-4 py-3 text-[14px] leading-7 text-foreground-700">
              {item.summary}
            </p>

            <div className="mt-5 space-y-4">
              {item.content.map((paragraph, index) => (
                <p
                  key={index}
                  className="text-[15px] md:text-[16px] leading-[1.95] text-foreground-800"
                >
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-6 rounded-md border border-background-200 bg-background-100 px-4 py-3 text-xs leading-6 text-foreground-500">
              本文为演示内容，人物与事件均为虚构。若你掌握相关线索，欢迎通过
              <Link to="/about" className="mx-1 text-primary-600 hover:underline cursor-pointer">
                线索提交页
              </Link>
              与我们联系。
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-background-200">
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-400">分享这条爆料</span>
                <ShareBar title={item.title} />
              </div>
              <Link
                to={`/category/${item.categorySlug}`}
                className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line" />
                返回{item.category}
              </Link>
            </div>
          </article>

          <aside className="w-full lg:w-[336px] shrink-0 space-y-5 lg:sticky lg:top-[92px]">
            {topic && (
              <section className="rounded-lg overflow-hidden border border-background-200/80 bg-background-50">
                <div className="w-full h-[130px] bg-background-200 relative">
                  <img
                    src={topic.cover}
                    alt={topic.name}
                    title={`${topic.name} - 专题合集`}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 to-transparent" />
                  <div className="absolute left-4 bottom-3 right-4">
                    <p className="font-label text-[11px] tracking-[0.2em] text-background-200/80">
                      RELATED TOPIC
                    </p>
                    <h2 className="mt-1 text-base font-bold text-background-50 truncate">
                      {topic.name}
                    </h2>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-xs leading-6 text-foreground-600 line-clamp-2-safe">
                    {topic.subtitle}
                  </p>
                  <Link
                    to={`/topic/${topic.id}`}
                    className="mt-3 inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    查看专题全部内容
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </section>
            )}

            <section className="rounded-lg border border-background-200/80 bg-background-50 overflow-hidden">
              <div className="px-4 h-12 flex items-center justify-between border-b border-background-200/80">
                <h2 className="text-[15px] font-bold text-foreground-950 flex items-center gap-2">
                  <i className="ri-article-line text-primary-500" />
                  相关推荐
                </h2>
                <span className="font-label text-[11px] tracking-[0.18em] text-foreground-400">
                  MORE
                </span>
              </div>
              <div className="px-4 py-1">
                {related.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/detail/${rel.id}`}
                    className="group block py-3.5 border-b border-background-200/60 last:border-b-0 cursor-pointer"
                  >
                    <h3 className="text-[13px] leading-6 font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2-safe">
                      {rel.title}
                    </h3>
                    <p className="mt-1 flex items-center gap-2 text-[11px] text-foreground-400">
                      <span>{formatDateTime(rel.publishedAt)}</span>
                      <span className="h-2.5 w-px bg-background-300" />
                      <span>{formatViews(rel.views)} 阅读</span>
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          </aside>
        </div>
      </div>
    </PageLayout>
  );
}